import { useMemo, useState } from "react"
import type { GridSortModel } from "@mui/x-data-grid-pro"
import { useUrlFilters, UrlFilterSchema } from "@core/hooks/useUrlFilters"
import { MAX_LIST_LIMIT, useUrlListState } from "@core/hooks/useUrlListState"
import {
  BOT_MONITOR_DEFAULTS,
  BOT_MONITOR_SORT_FIELDS,
  BOT_MONITOR_STATUSES,
} from "../constants/botMonitor"
import {
  BotMonitorFilters,
  BotMonitorQuery,
  BotMonitorStatus,
} from "../interfaces/botMonitor"
import {
  areBotMonitorFiltersEquivalent,
  validateBotMonitorFilters,
} from "../utils/botMonitorQuery"

const SCHEMA = {
  search: "string",
  status: "stringArray",
  archived: "string",
  page: "number",
  pageSize: "number",
  sortBy: "string",
  order: "string",
} as const satisfies UrlFilterSchema

export const useBotMonitorListState = () => {
  const { read, write } = useUrlFilters(SCHEMA)
  const raw = useMemo(read, [read])
  const list = useUrlListState({
    defaultPageSize: BOT_MONITOR_DEFAULTS.limit,
    defaultSortBy: BOT_MONITOR_DEFAULTS.sortBy,
    defaultOrder: BOT_MONITOR_DEFAULTS.order,
  })
  const limit = Number.isInteger(list.pageSize)
    ? list.pageSize
    : BOT_MONITOR_DEFAULTS.limit
  const page =
    Number.isSafeInteger(list.page) && Number.isSafeInteger(list.page * limit)
      ? list.page
      : 0
  const sortBy = BOT_MONITOR_SORT_FIELDS.includes(
    list.sortBy as BotMonitorQuery["sortBy"],
  )
    ? (list.sortBy as BotMonitorQuery["sortBy"])
    : BOT_MONITOR_DEFAULTS.sortBy
  const { order } = list
  const result = useMemo(() => {
    const checked = validateBotMonitorFilters({
      search: raw.search ?? "",
      status:
        raw.status?.length &&
        raw.status.every((status) =>
          BOT_MONITOR_STATUSES.includes(status as BotMonitorStatus),
        )
          ? (raw.status as BotMonitorStatus[])
          : [...BOT_MONITOR_STATUSES],
      archived: raw.archived === "true",
    })
    if (!checked.ok) return checked
    const { search, status, archived } = checked.filters
    const query: BotMonitorQuery = {
      limit,
      offset: page * limit,
      sortBy,
      order,
      archived,
      ...(search ? { search } : {}),
      ...(status.length === BOT_MONITOR_STATUSES.length ? {} : { status }),
    }
    return { ok: true as const, query }
  }, [raw, limit, page, sortBy, order])
  const queryArgs = useMemo(
    () => (result.ok ? { queryParams: result.query } : undefined),
    [result],
  )
  const [draftResetKey, setDraftResetKey] = useState(0)

  const applyFilters = (filters: BotMonitorFilters) => {
    const checked = validateBotMonitorFilters(filters)
    if (
      !checked.ok ||
      (result.ok &&
        areBotMonitorFiltersEquivalent(checked.filters, result.query))
    )
      return checked
    write({
      ...checked.filters,
      archived: String(checked.filters.archived),
      pageSize: limit === BOT_MONITOR_DEFAULTS.limit ? undefined : limit,
      sortBy,
      order,
    })
    return checked
  }
  const setOffset = (offset: number) => {
    if (
      !Number.isSafeInteger(offset) ||
      offset < 0 ||
      offset % limit !== 0 ||
      offset === page * limit
    )
      return
    list.setPaginationModel({ page: offset / limit, pageSize: limit })
  }
  const setLimit = (next: number) => {
    if (
      !Number.isInteger(next) ||
      next < 1 ||
      next > MAX_LIST_LIMIT ||
      next === limit
    )
      return
    list.setPaginationModel({ page: 0, pageSize: next })
  }
  const setSort = (model: GridSortModel) => {
    if (
      model[0] &&
      !BOT_MONITOR_SORT_FIELDS.includes(
        model[0].field as BotMonitorQuery["sortBy"],
      )
    )
      return
    list.onSortModelChange(model)
  }
  const reset = () => {
    setDraftResetKey((value) => value + 1)
    write({})
  }
  const sortModel: GridSortModel = [
    { field: sortBy, sort: order === "ASC" ? "asc" : "desc" },
  ]
  return {
    result,
    queryArgs,
    sortModel,
    draftResetKey,
    applyFilters,
    setLimit,
    setOffset,
    setSort,
    reset,
  }
}
